// Unified Inventory Models

// Base Inventory Item
export interface InventoryItem {
  id: string;
  productId: string;
  productName: string;
  productBarcode: string;
  locationId: string;
  locationName: string;
  quantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  status: InventoryStatus;
  lastUpdated: string;
  createdAt: string;
}

// Enhanced Inventory Overview Models
export interface InventoryOverviewItem {
  product_id: number;
  product_name: string;
  product_barcode: string;
  product_brand: string;
  product_category: string;
  product_image?: string;
  has_serial: boolean;
  warehouse_quantity: number;
  marketers_quantity: number;
  stores_quantity: number;
  total_quantity: number;
  total_locations: number;
  stock_status: string;
  has_stock: boolean;
  is_low_stock: boolean;
}

export interface LocationDetail {
  location_id: number;
  location_name: string;
  serial_number?: string;
}

export interface InventoryOverviewResponse {
  items: InventoryOverviewItem[];
  total_items: number;
  total_quantity: number;
  total_value: number;
  low_stock_count: number;
  out_of_stock_count: number;
}

// Inventory Status
export enum InventoryStatus {
  IN_STOCK = 'IN_STOCK',
  LOW_STOCK = 'LOW_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  RESERVED = 'RESERVED',
  DAMAGED = 'DAMAGED'
}

// Location
export interface Location {
  id: string;
  name: string;
  type: LocationType;
  description?: string;
  capacity?: number;
  isActive: boolean;
  createdAt: string;
}

export enum LocationType {
  WAREHOUSE = 'WAREHOUSE',
  STORE = 'STORE',
  BRANCH = 'BRANCH',
  SUPPLIER = 'SUPPLIER'
}

// Serial Tracking
export interface TrackedSerial {
  id: string;
  serialNumber: string;
  productId: string;
  productName: string;
  locationId: string;
  locationName: string;
  status: SerialStatus;
  purchaseDate?: string;
  warrantyExpiration?: string;
  assignedTo?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export enum SerialStatus {
  AVAILABLE = 'AVAILABLE',
  ASSIGNED = 'ASSIGNED',
  SOLD = 'SOLD'
}

/** Last sale from serial details */
export interface SerialLastSale {
  invoiceNumber?: string;
  storeName?: string;
  marketerName?: string;
  saleDate?: string;
}

/** Response from serial details API */
export interface SerialDetails {
  serialNumber: string;
  productName?: string;
  status?: string;
  locationName?: string;
  createdAt?: string;
  lastSale?: SerialLastSale;
}

/** Timeline item for serial history */
export interface TimelineItem {
  id: string;
  type: string;
  typeAr?: string;
  fromLocationName?: string;
  toLocationName?: string;
  performedByName: string;
  performedAt: string;
  notes?: string;
}

/** Response from serial timeline API */
export interface SerialTimelineResponse {
  serial?: string;
  currentLocation?: string;
  timeline: TimelineItem[];
  pagination?: { page?: number; limit?: number; total?: number; totalPages?: number };
}

// Inventory Movement
export interface InventoryMovement {
  id: string;
  type: MovementType;
  productId: string;
  productName: string;
  productBarcode: string;
  quantity: number;
  fromLocationId?: string;
  fromLocationName?: string;
  toLocationId?: string;
  toLocationName?: string;
  reason: string;
  referenceNumber?: string;
  performedBy: string;
  performedAt: string;
  notes?: string;
  cost?: number;
  serialNumbers?: string[];
}

export enum MovementType {
  STOCK_IN = 'STOCK_IN',
  STOCK_OUT = 'STOCK_OUT',
  TRANSFER = 'TRANSFER',
  ADJUSTMENT = 'ADJUSTMENT',
  RETURN = 'RETURN',
  DAMAGE = 'DAMAGE'
}

// Stock Management Forms
export interface AddStockForm {
  productId: number;
  locationId: number;
  quantity: number;
  cost?: number;
  createdBy: number;
  serialNumbers?: string[];
}

export interface ProductInventoryStatus {
  productId: string;
  productName: string;
  productBarcode: string;
  // Legacy fields (v1)
  totalQuantity?: number;
  availableQuantity?: number;
  reservedQuantity?: number;
  locations?: LocationInventory[];
  lowStockAlert?: boolean;
  outOfStockAlert?: boolean;

  // V2 fields (extended)
  hasSerial?: boolean;
  warehouseQuantity?: number;
  marketersQuantity?: number;
  storesQuantity?: number;
  summary?: {
    totalLocations: number;
    hasStock: boolean;
    stockStatus: string;
  };
  warehouseDetails?: LocationDetail[];
  marketersDetails?: LocationDetail[];
  storesDetails?: LocationDetail[];
}

export interface LocationInventory {
  locationId: string;
  locationName: string;
  quantity: number;
  availableQuantity: number;
  reservedQuantity: number;
}

// Filters
export interface InventoryFilters {
  locationId?: string;
  productId?: string;
  status?: InventoryStatus;
  minQuantity?: number;
  maxQuantity?: number;
  lowStockOnly?: boolean;
  outOfStockOnly?: boolean;
}

export interface InventoryMovementFilters {
  type?: MovementType;
  productId?: string;
  locationId?: string;
  performedBy?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface InventoryStatistics {
  totalProducts: number;
  productsWithStock: number;
  outOfStock: number;
  lowStock: number;
  availableStock: number;
  totalQuantity: number;
  productsNotInInventory: number;
  breakdown: {
    serialProducts: number;
    nonSerialProducts: number;
    productsInInventory: number;
  };
}

export interface SerialStatistics {
  totalSerials: number;
  availableSerials: number;
  assignedSerials: number;
  soldSerials: number;
}
